import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useGetSalesOrderQuery, usePrintInvoiceNetworkMutation } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { shareSalesOrderPdf, exportSalesOrderToPdf } from '../utils/exportSalesOrderPdf';
import qzService from '../../../shared/utils/qzService';
import { SalesInvoiceTemplateRenderer } from '../components/SalesInvoiceTemplateRenderer';

const TEMPLATE_OPTIONS = [
  { id: 'custom', label: '🎨 Custom Design Studio (Configured)' },
  { id: 'standard', label: 'Template 1 – Standard A4' },
  { id: 'thermal', label: 'Template 2 – Thermal' },
  { id: 'template3', label: 'Template 3 – Red Geometric Modern' },
  { id: 'template4', label: 'Template 4' },
  { id: 'template5', label: 'Template 5' },
  { id: 'template6', label: 'Template 6' },
  { id: 'template7', label: 'Template 7' },
  { id: 'template8', label: 'Template 8' },
  { id: 'template9', label: 'Template 9' },
  { id: 'template10', label: 'Template 10' },
  { id: 'template11', label: 'Template 11 – Tax Invoice Grid' },
  { id: 'a5', label: 'Template A5 – Tax Invoice (Compact Receipt)' },
  { id: 'a4_preprinted', label: 'Template A4 – Pre-printed Paper' },
  { id: 'a5_preprinted', label: 'Template A5 – Pre-printed Paper' },
];

function SalesOrderPrint() {
  const { id } = useParams();
  const { data: order, isLoading: loadingOrder, error } = useGetSalesOrderQuery(id);
  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();
  const isLoading = loadingOrder || loadingCompany;
  const [printNetwork, { isLoading: isPrinting }] = usePrintInvoiceNetworkMutation();

  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const paperType = company?.printPaperType || (isAndroid ? 'letter' : 'a4');

  const [templateSelection, setTemplateSelection] = useState('');
  const activeTemplate = templateSelection || company?.salesPrintTemplate || 'custom';

  const currency = company?.currency || 'USD';
  const itemFormat = company?.printItemNameFormat || 'name_sku';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const [showTax, setShowTax] = useState(true);
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);
  const [isQzPrinting, setIsQzPrinting] = useState(false);

  const handleQzPrint = async () => {
    if (!company?.qzPrinterName) {
      alert('No QZ Printer Name configured. Please select a printer in Settings.');
      return;
    }
    setIsQzPrinting(true);
    try {
      const printElement = document.querySelector('.print-content');
      const pdfArrayBuffer = await exportSalesOrderToPdf(order, company, {
        element: printElement,
        templateKey: activeTemplate,
        showTax: showTax,
        itemFormat: company?.printItemNameFormat || 'name_sku',
        filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
        save: false,
      });

      const base64Pdf = btoa(
        new Uint8Array(pdfArrayBuffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      await qzService.printPdf(company.qzPrinterName, base64Pdf, {
        jobName: `Invoice-${order.invoiceNumber || order.orderNumber}`,
      });
    } catch (err) {
      alert(`QZ Print failed: ${err.message}`);
    } finally {
      setIsQzPrinting(false);
    }
  };

  useEffect(() => {
    if (!isLoading && order && company?.enableAutoPrintOnSave && !hasAutoPrinted) {
      setHasAutoPrinted(true);
      if (company?.enableQzPrint && company?.qzPrinterName) {
        handleQzPrint();
      } else {
        setShowTax(true);
        setTimeout(() => {
          window.focus();
          window.print();
        }, 500);
      }
    }
  }, [order?.id, company?.id, isLoading, hasAutoPrinted]);

  const handleShare = () => {
    if (!order) return;
    const printElement = document.querySelector('.print-content');
    shareSalesOrderPdf(order, company, {
      element: printElement,
      templateKey: activeTemplate,
      showTax: showTax,
      itemFormat: company?.printItemNameFormat || 'name_sku',
      filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    });
  };

  const handleDirectPrint = async (silent = false) => {
    try {
      await printNetwork(id).unwrap();
      if (!silent) alert('Sent to network printer successfully');
    } catch (err) {
      const msg = err.data?.message || err.message;
      if (!silent) alert(`Print failed: ${msg}`);
      else console.error('Silent print failed:', msg);
    }
  };

  useEffect(() => {
    if (order && company?.enableNetworkThermalPrinter && company?.enableDirectPrintSilently && !isLoading) {
      handleDirectPrint(true);
    }
  }, [order?.id, company?.id, isLoading]);

  if (isLoading) return <Box sx={{ p: 4 }}><Typography>Loading sales invoice...</Typography></Box>;
  if (error || !order) return <Box sx={{ p: 4 }}><Typography color="error">Error loading sales order</Typography></Box>;

  const isA5 = activeTemplate === 'a5' || activeTemplate === 'a5_preprinted' || paperType === 'a5';
  const isThermal = activeTemplate === 'thermal';
  const pageWidth = isThermal ? '80mm' : (isA5 ? '148mm' : (paperType === 'letter' ? '215.9mm' : '210mm'));

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`
        @page { 
          size: ${isThermal ? '80mm auto' : (isA5 ? 'A5' : (paperType === 'letter' ? 'letter' : 'a4'))}; 
          margin: 0;
        }
        @media print {
          body { 
            margin: 0 !important; 
            padding: 0 !important; 
            background: #fff !important; 
          }
          .print-toolbar { 
            display: none !important; 
          }
          .print-content-wrapper {
            padding: 0 !important;
            background: #fff !important;
            box-shadow: none !important;
            min-height: auto !important;
          }
          .print-content {
            width: ${pageWidth} !important;
            max-width: ${pageWidth} !important;
            margin: 0 auto !important;
            padding: ${isA5 || activeTemplate === 'custom' || isThermal ? '0' : '8mm'} !important;
            border: none !important;
            box-sizing: border-box !important;
            background: #fff !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* Top Action Toolbar */}
      <Box 
        className="print-toolbar" 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          gap: 1.5, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          bgcolor: '#ffffff',
          p: 2,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <TextField
          select
          size="small"
          label="Print Template"
          value={activeTemplate}
          onChange={(e) => setTemplateSelection(e.target.value)}
          sx={{ minWidth: 260, bgcolor: 'background.paper' }}
        >
          {TEMPLATE_OPTIONS.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <Button 
          variant="contained" 
          startIcon={<PrintIcon />} 
          onClick={() => { setShowTax(true); setTimeout(() => window.print(), 100); }}
          sx={{ fontWeight: 600 }}
        >
          Print (with Tax)
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<PrintIcon />} 
          onClick={() => { setShowTax(false); setTimeout(() => window.print(), 100); }}
          sx={{ fontWeight: 600 }}
        >
          Print (No Tax)
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<PictureAsPdfIcon />} 
          onClick={handleShare}
          sx={{ fontWeight: 600 }}
        >
          Share / PDF
        </Button>

        {company?.enableQzPrint && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PrintIcon />}
            onClick={() => handleQzPrint()}
            disabled={isQzPrinting}
          >
            {isQzPrinting ? 'Printing...' : `Direct Print (QZ: ${company.qzPrinterName || 'Default'})`}
          </Button>
        )}

        {company?.enableNetworkThermalPrinter && (
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<PrintIcon />} 
            onClick={() => handleDirectPrint()}
            disabled={isPrinting}
          >
            {isPrinting ? 'Printing...' : 'Direct Print (Network)'}
          </Button>
        )}
      </Box>

      {/* Rendered Invoice Paper Container */}
      <Box 
        className="print-content-wrapper"
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-start' 
        }}
      >
        <Box 
          className="print-content" 
          sx={{ 
            width: '100%',
            maxWidth: isThermal ? '420px' : (isA5 ? '650px' : '900px'), 
            bgcolor: '#fff', 
            borderRadius: 2, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <SalesInvoiceTemplateRenderer
            order={order}
            company={company}
            templateKey={activeTemplate}
            currency={currency}
            itemFormat={itemFormat}
            dateFormat={dateFormat}
            showTax={showTax}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default SalesOrderPrint;
